import React, { useState, useEffect, useRef, useContext } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { InputSelector } from "../../UI/InputSelector";
import { getDeskByIdApi } from "../../../utils/api/Admin/desk/getDeskByIdApi";
import { updateDeskApi } from "../../../utils/api/Admin/desk/updateDeskApi";
import styles from "../../../styles/components/Modal/Employees/EditDeskModal.module.css";
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";

const translations = {
    ru: {
        editDesk: "Редактировать Деск",
        loading: "Загрузка информации о деске...",
        loadingError: (id) => `Не удалось загрузить информацию о деске с ID ${id}`,
        fetchError: (error) => `Ошибка при загрузке информации: ${error}`,
        generalUpdateError: (id) => `Произошла ошибка при обновлении деска с ID ${id}.`,
        updateError: (id) => `Ошибка при обновлении деска с ID ${id}:`,
        confirm: "Подтвердить",
        fields: {
            name: "Название",
            description: "Описание",
            id: "ID",
            createdAt: "Дата создания",
        }
    },
    en: {
        editDesk: "Edit Desk",
        loading: "Loading desk information...",
        loadingError: (id) => `Failed to load desk information with ID ${id}`,
        fetchError: (error) => `Error loading information: ${error}`,
        generalUpdateError: (id) => `An error occurred while updating desk with ID ${id}.`,
        updateError: (id) => `Error updating desk with ID ${id}:`,
        confirm: "Confirm",
        fields: {
            name: "Name",
            description: "Description",
            id: "ID",
            createdAt: "Date Created",
        }
    },
};

export const EditDeskModal = ({ handleEditDeskModal, fetchDesks, deskId }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const t = translations[langSelected] || translations.ru;

    const [formData, setFormData] = useState({
        name: "",
        description: "",
    });
    const [deskInfo, setDeskInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const modalRef = useRef(null);

    const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            handleEditDeskModal();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleEditDeskModal]);

    useEffect(() => {
        const loadDeskInfo = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getDeskByIdApi(deskId);
                setDeskInfo(data);
                setFormData({
                    name: data.name || "",
                    description: data.description || "",
                });
            } catch (err) {
                setError(err.message || t.loadingError(deskId));
            } finally {
                setLoading(false);
            }
        };

        if (deskId) {
            loadDeskInfo();
        }
    }, [deskId, t]);

    const handleChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        if (touched[key]) {
            setErrors((prev) => ({ ...prev, [key]: "" }));
        }
    };

    const handleFocus = (key) => {
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const handleBlur = (key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const handleSubmit = async () => {
        if (!deskId) return;

        const payload = {
            name: formData.name,
            description: formData.description || null,
        };

        try {
            await updateDeskApi(deskId, payload);
            handleEditDeskModal();
            await fetchDesks();
        } catch (err) {
            console.error(t.updateError(deskId), err);
            if (err && err.errors) {
                setErrors(err.errors);
            } else if (err && err.message) {
                setErrors({ general: err.message });
            } else {
                setErrors({ general: t.generalUpdateError(deskId) });
            }
        }
    };

    const fields = [
        { key: "name", label: t.fields.name },
        { key: "description", label: t.fields.description },
    ];

    if (loading) {
        return (
            <div className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}>
                <div className={`${styles.filtersContainer} ${isDark ? styles.darkFiltersContainer : ''}`} ref={modalRef}>
                    <div className={`${styles.modalLabel} ${isDark ? styles.darkModalLabel : ''}`}>
                        <p className={`${isDark ? styles.darkModalLabelText : ''}`}>{t.editDesk}</p>
                        <div onClick={handleEditDeskModal} className={`${isDark ? styles.darkModalLabelCloseButton : ''}`}><CloseIcon /></div>
                    </div>
                    <div className={`${styles.modalContent} ${isDark ? styles.darkModalContent : ''}`}>{t.loading}</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}>
                <div className={`${styles.filtersContainer} ${isDark ? styles.darkFiltersContainer : ''}`} ref={modalRef}>
                    <div className={`${styles.modalLabel} ${isDark ? styles.darkModalLabel : ''}`}>
                        <p className={`${isDark ? styles.darkModalLabelText : ''}`}>{t.editDesk}</p>
                        <div onClick={handleEditDeskModal} className={`${isDark ? styles.darkModalLabelCloseButton : ''}`}><CloseIcon /></div>
                    </div>
                    <div className={`${styles.modalContent} ${isDark ? styles.darkModalContent : ''} ${isDark ? styles.darkErrorText : ''}`}>{t.fetchError(error)}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}>
            <div className={`${styles.filtersContainer} ${isDark ? styles.darkFiltersContainer : ''}`} ref={modalRef}>
                <div className={`${styles.modalLabel} ${isDark ? styles.darkModalLabel : ''}`}>
                    <p className={`${isDark ? styles.darkModalLabelText : ''}`}>{t.editDesk}</p>
                    <div onClick={handleEditDeskModal} className={`${isDark ? styles.darkModalLabelCloseButton : ''}`}><CloseIcon /></div>
                </div>

                <div className={styles.modalContent}>
                    <div className={styles.section}>
                        {deskInfo?.id && (
                            <div className={`${styles.infoRow} ${isDark ? styles.darkInfoRow : ''}`}>
                                <label className={`${isDark ? styles.darkLabel : ''}`}>{t.fields.id}:</label>
                                <div className={`${isDark ? styles.darkInfoDiv : ''}`}>{deskInfo.id}</div>
                            </div>
                        )}

                        {deskInfo?.created_at && (
                            <div className={`${styles.infoRow} ${isDark ? styles.darkInfoRow : ''}`}>
                                <label className={`${isDark ? styles.darkLabel : ''}`}>{t.fields.createdAt}:</label>
                                <div className={`${isDark ? styles.darkInfoDiv : ''}`}>{deskInfo.created_at}</div>
                            </div>
                        )}

                        {fields.map(({ key, label }) => (
                            <div key={key} className={`${styles.inputRow} ${isDark ? styles.darkInputRow : ''}`}>
                                <label className={`${isDark ? styles.darkLabel : ''}`}>{label}:</label>
                                <InputSelector
                                    filterName={key}
                                    selectedValue={formData[key]}
                                    handleFilterChange={(k, v) => handleChange(k, v)}
                                    onFocus={() => handleFocus(key)}
                                    onBlur={() => handleBlur(key)}
                                    hasError={!!errors[key]}
                                    errorMessage={errors[key]}
                                    isTouched={touched[key]}
                                    isDark={isDark}
                                />
                            </div>
                        ))}
                        {errors.general && <div className={`${styles.generalError} ${isDark ? styles.darkGeneralError : ''}`}>{errors.general}</div>}
                    </div>
                </div>

                <div className={`${styles.modalFooter} ${isDark ? styles.darkModalFooter : ''}`}>
                    <div className={styles.confirmationWrapper}>
                        <button className={`${styles.submitButton} ${isDark ? styles.darkSubmitButton : ''}`} onClick={handleSubmit}>
                            {t.confirm}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};